import { bearerAuth, fetchTokenAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import { urlToPattern } from "../urlToPattern";
import logo from "./logo.svg";

const applicationAccountBaseUrl = (instanceUrl?: string) => {
  if (!instanceUrl?.trim()) return "";

  let url: URL;
  try {
    url = new URL(instanceUrl.trim());
  } catch {
    return "";
  }

  if (
    url.protocol !== "https:" ||
    url.username ||
    url.password ||
    url.search ||
    url.hash
  ) {
    return "";
  }

  return url.href.replace(/\/+$/, "");
};

export const secretServer = defineProduct({
  name: "Secret Server",
  tagLine: "Privileged access management and secret vault",
  logo,
  tags: ["security", "identity"],
  variants: [
    productVariant({
      identifier: "secretServer",
      authLabel: "OAuth access token",
      protocol: "http",
      auth: (app) => bearerAuth(app.secret.accessToken),
      steps: [
        {
          title: "Create a Secret Server OAuth access token",
          items: [
            {
              kind: "markdown",
              content:
                "In Secret Server, authenticate against the `oauth2/token` endpoint to get an OAuth 2.0 access token, then use that token as a bearer token for API requests.",
            },
          ],
        },
        {
          title: "Enter your Secret Server details",
          items: [
            {
              kind: "baseUrl",
              label: "Secret Server base URL",
              required: true,
              placeholder: "https://your-secret-server.example.com",
              target: { kind: "configuration", field: "instanceUrl" },
            },
            {
              kind: "input",
              label: "OAuth access token",
              required: true,
              target: { kind: "secrets", field: "accessToken" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: ({ instanceUrl }) =>
          `${instanceUrl || "https://your-secret-server.example.com"}/api/v1/folders`,
      },
      defaultUrlPattern: ({ instanceUrl }) => urlToPattern(instanceUrl, ""),
    }),
    productVariant({
      identifier: "secretServerApplicationAccount",
      authLabel: "Application account",
      protocol: "http",
      auth: (app) => {
        const baseUrl = applicationAccountBaseUrl(
          app.configuration.instanceUrl
        );
        return fetchTokenAuth({
          request: {
            url: baseUrl ? `${baseUrl}/oauth2/token` : "",
            method: "POST",
            formBody: {
              grant_type: "password",
              username: app.secret.username,
              password: app.secret.password,
            },
          },
          extract: { from: "jsonPath", paths: ["access_token"] },
          apply: { kind: "header", scheme: "Bearer" },
          cache: { ttlSeconds: 0 },
        });
      },
      steps: [
        {
          title: "Create a Secret Server application account",
          items: [
            {
              kind: "markdown",
              content:
                "Create an enabled, dedicated user in Secret Server and select **Application Account**. Assign only the roles and access to secrets needed for your workflows, not an administrator role. Application accounts are exempt from **Prevent direct API authentication**, so you do not need to disable that setting or grant a bypass permission. This option uses native username-and-password authentication and does not handle interactive OTP prompts or Windows integrated authentication. It does not use Delinea Platform service-user credentials.\n\nHave your Secret Server administrator ensure **Enable webservices** is enabled and set **Session timeout for webservices** to a short period. The connector obtains a new access token for each API request, so you do not need to paste or renew short-lived tokens or enable refresh tokens.\n\nThe connection test reads the current user summary without reading a secret. A successful test does not confirm permission to retrieve or update secrets. Confirm that your instance’s REST API guide supports `/api/v1/users/current-summary`.",
            },
          ],
        },
        {
          title: "Enter your application account details",
          items: [
            {
              kind: "baseUrl",
              label: "Secret Server base URL",
              required: true,
              placeholder:
                "https://your-secret-server.example.com/SecretServer",
              helperText:
                "Use your HTTPS Secret Server instance URL, including any installation path such as /SecretServer, but not /api or /oauth2/token. Do not include a username, password, query string, or fragment in the URL. This option uses the native REST API under /api/, not the Delinea Platform API.",
              target: { kind: "configuration", field: "instanceUrl" },
            },
            {
              kind: "input",
              label: "Application account username",
              required: true,
              helperText:
                "Use username or domain\\username, as appropriate for your account.",
              target: { kind: "secrets", field: "username" },
            },
            {
              kind: "input",
              label: "Application account password",
              required: true,
              helperText:
                "Use the password accepted by your instance’s native token endpoint. If Thycotic One is your default API authentication provider and this user has logged in through it, use the user’s Thycotic One password rather than the local Secret Server password.",
              target: { kind: "secrets", field: "password" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: ({ instanceUrl }) => {
          const baseUrl = applicationAccountBaseUrl(instanceUrl);
          return {
            url: baseUrl ? `${baseUrl}/api/v1/users/current-summary` : "",
            method: "GET",
            expectedStatus: 200,
            expectedBodyContains: '"id"',
          };
        },
      },
      defaultUrlPattern: ({ instanceUrl }) => {
        const baseUrl = applicationAccountBaseUrl(instanceUrl);
        return baseUrl ? `^${urlToPattern(`${baseUrl}/api`, "")}` : "";
      },
    }),
  ],
});
